import { useCallback, useEffect, useMemo, useState } from 'react';

import { FAIconName, useToastMessage } from '@equip.health/ui';

import {
  useAddPatientContactMutation,
  useAddSupportMutation,
  useDeletePatientSupportLinkMutation,
  useEditPatientContactMutation,
  useEditSupportByIdMutation,
  useGetSupportForEmailQuery,
} from '~/store/services/patients';

import { useGlobalContext } from '../contexts/GlobalContext';
import {
  applyPatientAddress,
  formatPhoneNumberWithCode,
  generateSupportPayload,
  getAddPatientContactPayload,
  getContactRequiredFields,
  isContactAddressFieldsEmpty,
  validateContactPhonesField,
} from '../util/patientContact.util';
import { EquipAppSupportPermissions } from '../constants/supports.constants';
import { convertToUSDateFormat } from '../util/patient.util';
import {
  PatientContactState,
  ShowEditDuplicateSupportModal,
} from '~/lib/types/patient/PatientContactState';

export const usePatientContactV2 = (
  contact: PatientContact,
  patientId: string,
  onAdd: () => void,
  patientBasicDetails: Patient,
  supportEmailAddresses: string[],
  onDeletePatientSupportLink?: () => void,
) => {
  const {
    contactExternalId,
    address,
    cellPhone: contactPhoneNumber,
    chosenName: contactChosenName,
    middleName: contactMiddleName,
    contactPreferences: patientContactPreferences,
    dateOfBirth: contactDOB,
    email: contactEmail,
    ethnicities: contactEthnicities,
    supportExternalId,
    firstName: contactFirstName,
    gender: contactGender,
    isGuarantor: contactIsGuarantor,
    isLivesWithPatient: contactLivesWithPatient,
    isOngoingProvider: contactIsOngoingProvider,
    isPrimaryContact,
    isReferringProvider: contactIsReferringProvider,
    languages: contactLanguages,
    lastName: contactLastName,
    prefix: contactPrefix,
    suffix: contactSuffix,
    officeExtension: officeExtensionNumber,
    officePhone,
    pronouns: contactPronouns,
    relationToPatient: patientRelation,
    timezone: contactTimeZone,
    isProxy,
    isSupport,
    faxNumber: contactFaxNumber,
    homePhone: contactHomePhone,
    secondaryCellPhone: contactSecondaryPhoneNumber,
  } = contact ?? {};
  const livesWithPatient = contactLivesWithPatient ? 'Yes' : 'No';
  const isProxyCheck = isProxy
    ? EquipAppSupportPermissions.PROXY
    : EquipAppSupportPermissions.GENERAL;
  const { createToast } = useToastMessage();
  const { currentUser } = useGlobalContext();
  const [formData, setFormData] = useState<PatientContactState>({
    address: {
      addressLine1: address?.addressLine1 || '',
      addressLine2: address?.addressLine2 || '',
      city: address?.city || '',
      country: '',
      latitude: address?.latitude,
      longitude: address?.longitude,
      name: '',
      state: address?.state || '',
      timezone: address?.timezone || contactTimeZone || '',
      zipCode: address?.zip || '',
    },
    contactAddress: {
      livesWithPatient:
        contactLivesWithPatient !== undefined ? livesWithPatient : '',
      organization: address?.affiliationName || '',
    },
    contactExternalId,
    contactInfo: {
      contactPreference: patientContactPreferences || [],
      email: contactEmail || '',
      invalidFields: {
        email: false,
        relationToPatient: false,
      },
      isDuplicateEmail: false,
      languages: contactLanguages || [],
      relationToPatient: patientRelation || '',
    },
    emailChangeWarningModalOpen: false,

    hasUnsavedChanges: false,
    isError: false,
    isLinkedAccountsModalVisible: false,
    isLoading: false,
    patientId,
    personalInfo: {
      chosenName: contactChosenName || '',
      dateOfBirth: (contactDOB && convertToUSDateFormat(contactDOB)) || '',
      ethnicities: contactEthnicities || [],
      firstName: contactFirstName || '',
      gender: contactGender || '',
      invalidFields: {
        chosenName: false,
        dateOfBirth: false,
        firstName: false,
        lastName: false,
        middleName: false,
        permissions: false,
        prefix: false,
        suffix: false,
      },
      isEmergencyContact: contact?.isEmergencyContact || false,
      isGrantEquipAccess: isSupport || false,
      isGuarantor: contactIsGuarantor || false,
      isOngoingProvider: contactIsOngoingProvider || false,
      isPrimaryContact: isPrimaryContact || false,
      isReferringProvider: contactIsReferringProvider || false,
      lastName: contactLastName || '',
      middleName: contactMiddleName || '',
      permissions: isSupport ? isProxyCheck : '',
      prefix: contactPrefix || '',
      pronouns: contactPronouns || [],
      suffix: contactSuffix || '',
    },
    phonesField: {
      extension: officeExtensionNumber || '',
      fax: formatPhoneNumberWithCode(contactFaxNumber) || '',
      home: formatPhoneNumberWithCode(contactHomePhone) || '',
      mobile: formatPhoneNumberWithCode(contactPhoneNumber) || '',
      office: formatPhoneNumberWithCode(officePhone) || '',
      other: formatPhoneNumberWithCode(contactSecondaryPhoneNumber) || '',
    },
    shouldRemoveSupportAppAccess: false,
    showEditDuplicateSupportModal: null,
  });

  const requiredPersonalInfoFields: (keyof ContactPersonalInfo)[] = [
    'firstName',
    'lastName',
  ];

  const requiredContactFields: (keyof ContactInfoType)[] = [
    'relationToPatient',
  ];

  const [
    addSupport,
    { isLoading: isAddSupportLoading, isError: addSupportError },
  ] = useAddSupportMutation();
  const [addContact, { isLoading: isAddContactLoading }] =
    useAddPatientContactMutation();
  const [editContact, { isLoading: isEditContactLoading }] =
    useEditPatientContactMutation();
  const [editSupportById, { isLoading: isEditSupportLoading }] =
    useEditSupportByIdMutation();
  const [deletePatientSupportLink] = useDeletePatientSupportLinkMutation();
  const shouldSkipSupportEmailQuery = !(
    formData.contactInfo.isDuplicateEmail && addSupportError
  );
  const { data: supportEmailData } = useGetSupportForEmailQuery(
    formData.contactInfo.email,
    {
      skip: shouldSkipSupportEmailQuery,
    },
  );
  const memoizedAddressFieldsEmpty = useMemo(
    () => isContactAddressFieldsEmpty(formData.address),
    [formData.address],
  );

  const updateField = useCallback(
    (
      field: string,
      value:
        | string
        | boolean
        | string[]
        | GooglePlacesField
        | ShowEditDuplicateSupportModal,
    ) => {
      if (field === 'showEditDuplicateSupportModal' && !value) {
        setFormData((prev) => ({
          ...prev,
          contactInfo: {
            ...prev.contactInfo,
            isDuplicateEmail: false,
          },
        }));
      }

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [setFormData],
  );

  useEffect(() => {
    const newAddress = applyPatientAddress(patientBasicDetails);

    const isAddressChanged = Object.keys(newAddress).some(
      (key) => newAddress[key] !== formData.address[key],
    );

    if (formData.contactAddress.livesWithPatient === 'Yes') {
      if (memoizedAddressFieldsEmpty) {
        setFormData((prev) => ({
          ...prev,
          address: newAddress,
        }));
      }
    } else if (formData.contactAddress.livesWithPatient === 'No') {
      if (!isAddressChanged) {
        setFormData((prev) => ({
          ...prev,
          address: {
            addressLine1: '',
            addressLine2: '',
            city: '',
            country: '',
            latitude: 0,
            longitude: 0,
            name: '',
            state: '',
            timezone: '',
            zipCode: '',
          },
        }));
      }
    }
  }, [
    memoizedAddressFieldsEmpty,
    formData.contactAddress.livesWithPatient,
    patientBasicDetails,
    formData.address,
  ]);

  useEffect(() => {
    updateField(
      'isLoading',
      isAddSupportLoading ||
        isAddContactLoading ||
        isEditContactLoading ||
        isEditSupportLoading,
    );
  }, [
    isAddSupportLoading,
    isAddContactLoading,
    isEditContactLoading,
    isEditSupportLoading,
    updateField,
  ]);

  useEffect(() => {
    if (
      contact?.isSupport &&
      formData.personalInfo.isGrantEquipAccess === false
    ) {
      updateField('shouldRemoveSupportAppAccess', true);
    } else {
      updateField('shouldRemoveSupportAppAccess', false);
    }
  }, [formData.personalInfo.isGrantEquipAccess, contact, updateField]);

  useEffect(() => {
    if (
      formData.contactInfo.isDuplicateEmail &&
      addSupportError &&
      supportEmailData
    ) {
      updateField('showEditDuplicateSupportModal', {
        enteredContact: generateSupportPayload(formData),
        existingSupport: supportEmailData,
      });
    }
  }, [
    addSupportError,
    formData,
    formData.contactInfo.isDuplicateEmail,
    supportEmailData,
    updateField,
  ]);

  const isEmailSameAsExistingSupportEmail = useMemo<boolean>(
    () =>
      !contact?.isSupport &&
      formData.personalInfo.isGrantEquipAccess &&
      formData.contactInfo.email?.length &&
      (supportEmailAddresses ?? []).includes(
        (formData.contactInfo.email ?? '')?.trim(),
      ),
    [
      contact?.isSupport,
      formData.contactInfo.email,
      formData.personalInfo.isGrantEquipAccess,
      supportEmailAddresses,
    ],
  );

  const isEmailSameAsPatientEmail = useMemo<boolean>(
    () =>
      patientBasicDetails?.email?.length &&
      formData.contactInfo.email === patientBasicDetails.email,
    [formData.contactInfo.email, patientBasicDetails?.email],
  );

  type EditableSections = Omit<
    PatientContactState,
    | 'patientId'
    | 'isLoading'
    | 'isError'
    | 'emailChangeWarningModalOpen'
    | 'hasUnsavedChanges'
    | 'isLinkedAccountsModalVisible'
    | 'shouldRemoveSupportAppAccess'
    | 'showEditDuplicateSupportModal'
    | 'contactExternalId'
  >;

  const updateSection = useCallback(
    <T extends keyof EditableSections, K extends keyof EditableSections[T]>(
      section: T,
      field: K,
      value: EditableSections[T][K],
    ) => {
      updateField('hasUnsavedChanges', true);
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    },
    [updateField, setFormData],
  );

  const updateInvalidFields = (
    section: 'personalInfo' | 'contactInfo',
    field: string,
    invalid: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        invalidFields: { ...prev[section].invalidFields, [field]: invalid },
      },
    }));
  };

  const isSupportEmailEdited = useMemo<boolean>(
    () => isSupport && formData.contactInfo.email !== contactEmail,
    [contactEmail, formData.contactInfo.email, isSupport],
  );

  useEffect(() => {
    if (isSupportEmailEdited) {
      if (!formData.personalInfo.isGrantEquipAccess) {
        updateSection('personalInfo', 'isGrantEquipAccess', true);
      }
      if (
        isProxy &&
        formData.personalInfo.permissions === EquipAppSupportPermissions.GENERAL
      ) {
        updateSection(
          'personalInfo',
          'permissions',
          EquipAppSupportPermissions.PROXY,
        );
      } else if (
        !isProxy &&
        formData.personalInfo.permissions === EquipAppSupportPermissions.PROXY
      ) {
        updateSection(
          'personalInfo',
          'permissions',
          EquipAppSupportPermissions.GENERAL,
        );
      }
    }
  }, [
    formData.personalInfo.isGrantEquipAccess,
    formData.personalInfo.permissions,
    isProxy,
    isSupportEmailEdited,
    updateSection,
  ]);

  const deletePatientSupport = () => {
    deletePatientSupportLink({
      patientId,
      supportExternalId: contact?.supportExternalId,
    })
      .unwrap()
      .then(() => {
        updateField('hasUnsavedChanges', false);
        onDeletePatientSupportLink();
      })
      .catch(() => {
        updateField('isError', true);
      });
  };

  const validateRequiredFields = (): boolean => {
    const updatedPersonalInvalidFields: Record<string, boolean> = {};
    const updatedContactInvalidFields: Record<string, boolean> = {};

    if (
      formData.personalInfo.isGrantEquipAccess &&
      !formData.personalInfo.permissions
    ) {
      updatedPersonalInvalidFields.permissions = true;
    }
    if (
      formData.personalInfo.isEmergencyContact &&
      !formData.phonesField.mobile
    ) {
      updatedContactInvalidFields.mobile = true;
    }
    if (
      formData.personalInfo.isGrantEquipAccess &&
      !formData.contactInfo.email
    ) {
      updatedContactInvalidFields.email = true;
    }

    requiredPersonalInfoFields.forEach((field) => {
      updatedPersonalInvalidFields[field] = !formData.personalInfo[field];
    });

    requiredContactFields.forEach((field) => {
      updatedContactInvalidFields[field] = !formData.contactInfo[field];
    });

    Object.entries(updatedPersonalInvalidFields).forEach(([field, invalid]) => {
      updateInvalidFields('personalInfo', field, invalid);
    });

    Object.entries(updatedContactInvalidFields).forEach(([field, invalid]) => {
      updateInvalidFields('contactInfo', field, invalid);
    });

    const isPersonalInfoValid = Object.values(
      updatedPersonalInvalidFields,
    ).every((isValid) => !isValid);
    const isContactInfoValid = Object.values(updatedContactInvalidFields).every(
      (isValid) => !isValid,
    );

    const requiredAddressFields = getContactRequiredFields(formData.address);
    const isAddressValid = requiredAddressFields.every(
      (field) => formData.address[field],
    );

    const isValidPhonesField = validateContactPhonesField(
      formData.phonesField,
      formData.personalInfo.isGrantEquipAccess,
    );
    return (
      isPersonalInfoValid &&
      isContactInfoValid &&
      isAddressValid &&
      isValidPhonesField
    );
  };

  const supportApiError = (error) => {
    if (
      typeof error?.data === 'string' &&
      error?.data?.includes('Duplicate email found')
    ) {
      updateSection('contactInfo', 'isDuplicateEmail', true);
    } else updateField('isError', true);
  };

  const editOrAddPatientSupport = () => {
    const isValid = validateRequiredFields();

    if (!isValid) return;
    const payload = generateSupportPayload(formData);
    if (isSupport) {
      editSupportById({
        supportExternalId,
        body: payload,
      })
        .unwrap()
        .then(() => {
          if (formData.shouldRemoveSupportAppAccess) {
            deletePatientSupport();
          } else {
            updateField('hasUnsavedChanges', false);
            onAdd();
          }
          createToast({
            icon: FAIconName.checkRegular,
            id: 'add-contact-toast',
            text: 'Contact updated.',
            variant: 'success',
          });
        })
        .catch((error) => {
          supportApiError(error);
        });
    } else {
      addSupport(payload)
        .unwrap()
        .then(() => {
          if (formData.shouldRemoveSupportAppAccess) {
            deletePatientSupport();
          } else {
            updateField('hasUnsavedChanges', false);
            onAdd();
          }
          createToast({
            icon: FAIconName.checkRegular,
            id: 'add-contact-toast',
            text: 'Contact added.',
            variant: 'success',
          });
        })
        .catch((error) => {
          supportApiError(error);
        });
    }
  };

  const editOrAddPatientContact = () => {
    const isValid = validateRequiredFields();
    if (!isValid) return;
    const payload = getAddPatientContactPayload(formData, currentUser.userId);
    if (contactExternalId) {
      editContact({ body: payload, contactId: contactExternalId })
        .unwrap()
        .then(() => {
          updateField('hasUnsavedChanges', false);
          createToast({
            icon: FAIconName.checkRegular,
            id: 'edit-contact-toast',
            text: 'Contact updated.',
            variant: 'success',
          });
          onAdd();
        })
        .catch(() => {
          updateField('isError', true);
        });
    } else {
      addContact({ body: payload, patientId })
        .unwrap()
        .then(() => {
          updateField('hasUnsavedChanges', false);
          createToast({
            icon: FAIconName.checkRegular,
            id: 'add-contact-toast',
            text: 'Contact added.',
            variant: 'success',
          });
          onAdd();
        })
        .catch(() => {
          updateField('isError', true);
        });
    }
  };

  const handleSubmitContactInfo = () => {
    if (formData.emailChangeWarningModalOpen) {
      updateField('emailChangeWarningModalOpen', false);
    }
    if (formData.personalInfo.isGrantEquipAccess || contact?.isSupport) {
      editOrAddPatientSupport();
    } else {
      editOrAddPatientContact();
    }
  };

  const handleSave = () => {
    if (isSupportEmailEdited) {
      updateField('emailChangeWarningModalOpen', true);
    } else {
      handleSubmitContactInfo();
    }
  };

  return {
    formData,
    handleSave,
    handleSubmitContactInfo,
    isEmailSameAsExistingSupportEmail,
    isEmailSameAsPatientEmail,
    isSupportEmailEdited,
    updateField,
    updateInvalidFields,
    updateSection,
    validateRequiredFields,
  };
};
